import React, {useRef} from 'react';
import {Box, Card, CardContent, Typography, Checkbox, IconButton, Grid2} from '@mui/material';
import {useDrag, useDrop} from 'react-dnd';
import prettyBytes from 'pretty-bytes';
import FolderIcon from '@mui/icons-material/Folder';
import OpenWithIcon from '@mui/icons-material/OpenWith';

import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import {FileData} from '../types';
import {useFileContext} from '../context/FileContext';
import {scaleSize} from 'utils';
import {DELETE_FILE_MSG, DELETE_FOLDER_MSG, TILE_SIZE_INIT_VALUE} from 'const';
import ConfirmationWrapper from './buttons/ConfirmationWrapper';
import {FileIcon} from 'utils/FileIcon';
import {useTranslation} from 'react-i18next';
import {useFileMenuContext} from './context-menu/fileMenuContext';
import classNames from 'classnames';

export const FileFolderCard = ({file}: {file: FileData}) => {
   const {t} = useTranslation();
   const {
      create: {openRename},
      move: {moveItem, move},
      downloadEncrypted,
      remove,
      loadFolder,
      selectedFiles,
      setSelectedFiles,
      tileSize,
   } = useFileContext();

   const onFileClick = async (file: FileData) => {
      if (file.type === 'folder') {
         await loadFolder(file.path);
      }
   };

   // Handle checkbox toggle
   const handleToggle = (file: FileData) => {
      const isSelected = selectedFiles.some((selected) => selected.id === file.id);
      const updatedSelection = isSelected
         ? selectedFiles.filter((selected) => selected.id !== file.id)
         : [...selectedFiles, file];
      setSelectedFiles(updatedSelection);
   };

   const {handleClick, handleClose} = useFileMenuContext();
   React.useEffect(() => handleClose, [handleClose]);

   const ref = useRef(null);

   const [{isDragging}, drag] = useDrag({
      // canDrag: isPermitted(DiplomatObjectAceType.enum.REMOVE_OBJECT),
      type: 'file',
      item: file,
      collect: (monitor) => ({
         isDragging: monitor.isDragging(),
      }),
   });

   const [{canDrop, isOver}, drop] = useDrop(() => ({
      accept: 'file',
      drop: (draggedItem: FileData, monitor) => {
         if (!ref.current) return;

         if (draggedItem.path !== file.path) {
            move(file.path, draggedItem);
         }
      },

      canDrop: (item: FileData) => file.type === 'folder' && file.name !== item.name,
      collect: (monitor) => ({
         isOver: !!monitor.isOver(),
         canDrop: !!monitor.canDrop(),
      }),
   }));

   drag(drop(ref));

   return (
      <Card
         ref={ref}
         onContextMenu={handleClick(file)}
         key={file.id}
         onDoubleClick={() => onFileClick(file)}
         style={{opacity: isDragging ? 0.5 : 1}}
         className={classNames({
            'dnd-droppable': canDrop,
            'dnd-highlighted': isOver,
         })}
         sx={{
            cursor: 'pointer',
            padding: 2,
            position: 'relative',
            boxShadow: 2,
            borderRadius: 2,
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {transform: 'scale(1.05)'},

            '& .action-btns': {
               display: 'none',
            },
            '&:hover .action-btns': {
               display: 'flex',
            },
            '& .file-name-nav-link': {
               textOverflow: 'ellipsis',
               textAlign: 'center',
               width: '100%',
               overflow: 'hidden',
               '&:hover': {
                  textDecoration: 'underline',
                  cursor: 'pointer',
               },
            },
         }}
      >
         {file.type === 'file' && (
            <Checkbox
               checked={selectedFiles.some((selected) => selected.id === file.id)}
               onChange={() => handleToggle(file)}
               sx={{position: 'absolute', top: 8, right: 8}}
            />
         )}
         <Grid2
            className="action-btns"
            sx={{position: 'absolute', bottom: 8, left: 2, width: '100%'}}
            container
         >
            {file.type === 'file' && (
               <Grid2 size={{xs: 3}}>
                  <IconButton
                     className="dwnld-btn"
                     color="primary"
                     onClick={() => downloadEncrypted(file.path)}
                  >
                     <DownloadIcon />
                  </IconButton>
               </Grid2>
            )}
            <Grid2 size={{xs: 3}}>
               <IconButton className="edit-btn" onClick={() => openRename(file)} color="secondary">
                  <EditIcon />
               </IconButton>
            </Grid2>

            <Grid2 size={{xs: 3}}>
               <IconButton className="move-btn" onClick={() => moveItem(file)} color="secondary">
                  <OpenWithIcon />
               </IconButton>
            </Grid2>

            <Grid2 size={{xs: 3}}>
               <ConfirmationWrapper
                  onOk={() => remove(file)}
                  content={
                     t(file.type === 'file' ? DELETE_FILE_MSG : DELETE_FOLDER_MSG) +
                     '\n' +
                     file.name
                  }
               >
                  <IconButton className="remove-btn" color="warning">
                     <DeleteIcon />
                  </IconButton>
               </ConfirmationWrapper>
            </Grid2>
         </Grid2>
         <CardContent>
            <Box display="flex" flexDirection="column" alignItems="center">
               <Box
                  sx={{
                     height: tileSize + 'px', // '5rem',
                     width: tileSize + 'px', //'100%',
                     textAlign: 'center',
                     '&>svg': {height: '100%', width: '100%'},
                     // ...tileStyles[tileSize],
                  }}
               >
                  {file.type === 'folder' ? (
                     <FolderIcon fontSize="large" color="secondary" />
                  ) : (
                     // <InsertDriveFileIcon fontSize="large" color="primary" />
                     <FileIcon
                        name={file.name}
                        width={scaleSize(100, TILE_SIZE_INIT_VALUE, tileSize)}
                        alt="file icon"
                     />

                     // <img src={pdfSvg} width={120} alt="file icon" />
                     // getIconByName(file.name)
                  )}
               </Box>
               <Typography
                  className="file-name-nav-link"
                  variant="subtitle1"
                  fontSize={scaleSize(1, TILE_SIZE_INIT_VALUE, tileSize) + 'rem'}
                  onClick={() => onFileClick(file)}
                  title={file.name}
               >
                  {file.name}
               </Typography>
               {file.type === 'file' && (
                  <Box sx={{marginTop: '.5rem'}}>
                     <Typography
                        component="div"
                        variant="caption"
                        color="text.secondary"
                        // whiteSpace="pre-wrap"
                        fontSize={scaleSize(0.75, TILE_SIZE_INIT_VALUE, tileSize) + 'rem'}
                        textAlign="center"
                     >
                        Modified: {new Date(file.modifiedAt!).toLocaleDateString?.()}
                     </Typography>
                     <Typography
                        component="div"
                        variant="caption"
                        color="text.secondary"
                        fontSize={scaleSize(0.75, TILE_SIZE_INIT_VALUE, tileSize) + 'rem'}
                        textAlign="center"
                        marginTop=".2rem"
                     >
                        Size: {prettyBytes(Number(file.size))}
                     </Typography>
                  </Box>
               )}
            </Box>
         </CardContent>
      </Card>
   );
};
